import React from "react";
import { Button } from "antd";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import { PricingWrap, CardsRow, PlanCard, FootNote } from "./style";

// Priced per candidate workflow, not per micro-action: recruiters buy "AI
// handles this candidate end to end", so one workflow bundles the resume
// analysis, the outreach emails, the stage moves and the follow-up Q&A.
// Plans differ only in how many candidates a month they cover, plus the
// rate for anything past that.
const PLANS = [
  {
    name: "Starter",
    tagline: "For small teams trying AI on their first few roles.",
    price: "$49",
    period: "/ month",
    note: "billed monthly, per company",
    highlight: false,
    usage: [
      { label: "AI candidate workflows", value: "50 / mo" },
      { label: "Extra workflows", value: "$0.80 each" },
    ],
    features: [
      "Resume analysis — fit score, strengths & gaps",
      "Personalised outreach emails drafted per candidate",
      "Stage-move recommendations you approve in one click",
      "Candidate Q&A — up to 5 questions per candidate",
      "AI job description & screening question generator",
      "AI resume auto-fill — candidates skip typing the apply form",
      "Email support",
    ],
    cta: "Get Started",
  },
  {
    name: "Growth",
    tagline: "For teams hiring continuously, with AI in the daily workflow.",
    price: "$149",
    period: "/ month",
    note: "billed monthly, per company",
    highlight: true,
    usage: [
      { label: "AI candidate workflows", value: "200 / mo" },
      { label: "Extra workflows", value: "$0.60 each" },
    ],
    features: [
      "Everything in Starter",
      "Batch ranking — score every candidate for a job in one run",
      "Bulk outreach — draft and send to your top matches together",
      "Batch stage moves — shortlist or advance a whole group at once",
      "Auto mode — approved actions run without confirming each one",
      "Talent pool AI flagging",
      "Priority support",
    ],
    cta: "Upgrade to Growth",
  },
  {
    name: "Scale",
    tagline: "For high-volume hiring across many open roles.",
    price: "$399",
    period: "/ month",
    note: "billed monthly, per company",
    highlight: false,
    usage: [
      { label: "AI candidate workflows", value: "600 / mo" },
      { label: "Extra workflows", value: "$0.50 each" },
    ],
    features: [
      "Everything in Growth",
      "Lowest overage rate as your volume grows",
      "Usage analytics per recruiter and per job",
      "Custom hiring-process AI tuning",
      "Dedicated onboarding",
      "SLA-backed priority support",
    ],
    cta: "Choose Scale",
  },
];

function AIPricing() {
  const { t } = useTranslation();

  return (
    <PricingWrap>
      <div className="hero">
        <div className="eyebrow">
          <AutoAwesomeIcon style={{ fontSize: 14 }} />
          {t("Recruitment AI")}
        </div>
        <div className="heading">{t("Pricing for AI-powered hiring")}</div>
        <div className="subheading">
          {t(
            "One workflow = one candidate handled end to end: resume analysis, personalised outreach emails, stage-move recommendations and follow-up Q&A. Pick the plan that matches how many candidates you put through AI each month. Billed separately from your core Foresyts HR plan."
          )}
        </div>
      </div>

      <CardsRow>
        {PLANS.map((plan) => (
          <PlanCard key={plan.name} $highlight={plan.highlight}>
            {plan.highlight && <div className="badge">{t("Most Popular")}</div>}
            <div className="plan-name">{t(plan.name)}</div>
            <div className="plan-tagline">{t(plan.tagline)}</div>

            <div className="price-row">
              <span className="price">{plan.price}</span>
              {plan.period && <span className="price-period">{t(plan.period)}</span>}
            </div>
            <div className="price-note">{t(plan.note)}</div>

            <Button
              type={plan.highlight ? "primary" : "default"}
              className="cta-btn"
              style={
                plan.highlight
                  ? { background: "#2D53DA", borderColor: "#2D53DA" }
                  : { borderColor: "#2D53DA", color: "#2D53DA" }
              }
            >
              {t(plan.cta)}
            </Button>

            <div className="usage-box">
              {plan.usage.map((u) => (
                <div className="usage-row" key={u.label}>
                  <span>{t(u.label)}</span>
                  <strong>{u.value === "Unlimited" ? t("Unlimited") : u.value}</strong>
                </div>
              ))}
            </div>

            <div className="features-label">{t("What's included")}</div>
            <ul className="feature-list">
              {plan.features.map((f) => (
                <li key={f}>
                  <CheckOutlinedIcon />
                  <span>{t(f)}</span>
                </li>
              ))}
            </ul>
          </PlanCard>
        ))}
      </CardsRow>

      <FootNote>
        {t(
          "A workflow is only counted when the AI actually processes a candidate — browsing, searching and asking about candidates you've already analysed are free. Workflows reset monthly, and anything past your allowance is billed at your plan's rate, so you never jump a tier unexpectedly. Hiring more than 600 candidates a month or need an SLA? Contact sales for Enterprise volume pricing."
        )}
      </FootNote>
    </PricingWrap>
  );
}

export default AIPricing;
